import type { IArticle } from "~/types";
import {
  getGridStyles,
  getResult,
  getUniqueTileIds,
  isSingleTileGrid,
  type Grid,
  type GridTileMeta
} from "./layoutEngine";

/** Match rt-magazine-next Content gutter (`spacing.sm`). */
export const MAGAZINE_GUTTER = "16px";
export const MAGAZINE_PAGE_SIZE = 20;

/**
 * Column count used for the server render and the client's first paint.
 * Content width is unknown until mount, so the grid cannot wait on measurement
 * if article titles must be in the HTML.
 */
export const MAGAZINE_SSR_GRID_SIZE = 3;

/** Single-column (mobile): shorter rows so sparse cards don't look padded. */
export const MAGAZINE_MOBILE_MIN_ROW_HEIGHT = "180px";
export const MAGAZINE_MOBILE_MAX_ROW_HEIGHT = "420px";

export const getMagazineRowHeight = (
  gridSize: number,
  gutter = MAGAZINE_GUTTER
): string =>
  gridSize <= 1
    ? `calc(${100 / 4}vh - ${gutter})`
    : `calc(${100 / 3}vh - ${gutter})`;

/** CSS grid template areas require ids that start with a letter. */
export const getMagazineLayoutArticleId = (article: IArticle): string =>
  `ID${article.id}`;

export const getMagazineLayoutScore = (article: IArticle): number =>
  article.score ?? article.impactV5?.citations ?? 0;

/** Match feed-frontend ArticleGrid: shrink tiles without image and abstract. */
export const shouldPenalizeMagazineArticle = (article: IArticle): boolean =>
  !article.image?.url && !article.imageUrl && !article.abstract;

export type MagazineLayoutTile = {
  id: string;
  article: IArticle;
  meta: GridTileMeta;
  isFullRow: boolean;
};

export type MagazineLayoutBlock = {
  grid: Grid;
  styles: Record<string, string>;
  tiles: MagazineLayoutTile[];
};

/**
 * Builds magazine article grid blocks using local layout engine
 */
export function buildMagazineArticleLayout(
  articles: IArticle[],
  gridSize: number,
  gutter = MAGAZINE_GUTTER
): MagazineLayoutBlock[] {
  try {
    if (!articles.length || gridSize < 1) {
      return [];
    }

    const grids = getResult(articles, gridSize, {
      badnessThreshold: 0.1,
      getId: getMagazineLayoutArticleId,
      getScore: getMagazineLayoutScore,
      penalize: shouldPenalizeMagazineArticle,
      optimize: true,
      split: true
    });

    if (!grids?.length) {
      return [];
    }

    const articlesById = Object.fromEntries(
      articles.map((article) => [getMagazineLayoutArticleId(article), article])
    );

    return grids.flatMap((grid) => {
      if (!grid?.layout?.length) {
        return [];
      }

      try {
        const isMobileGrid = gridSize <= 1;
        const fullRow = isSingleTileGrid(grid);
        const styles = fullRow
          ? {
              // Match feed-frontend ArticleFullRow: content-sized full-width row.
              // Avoid flex:1 — it zero-heights empty tiles and stretches siblings.
              width: "100%"
            }
          : getGridStyles(grid, {
              width: "100%",
              rowHeight: getMagazineRowHeight(gridSize, gutter),
              gap: gutter,
              ...(isMobileGrid
                ? {
                    minRowHeight: MAGAZINE_MOBILE_MIN_ROW_HEIGHT,
                    maxRowHeight: MAGAZINE_MOBILE_MAX_ROW_HEIGHT
                  }
                : {})
            });
        const tiles = getUniqueTileIds(grid).flatMap((id) => {
          const article = articlesById[id];
          const meta = grid.meta?.tiles?.[id];
          if (!article || !meta) {
            return [];
          }
          return [
            {
              id,
              article,
              meta,
              isFullRow: fullRow
            } satisfies MagazineLayoutTile
          ];
        });

        return [{ grid, styles, tiles }];
      } catch {
        return [];
      }
    });
  } catch {
    // layout-engine can throw (e.g. no fit / unset bestSolution) — don't surface a 500
    return [];
  }
}

/** Content-width breakpoints used by feed-frontend RT magazine Content. */
export function getMagazineGridSize(contentWidth: number): number {
  if (contentWidth > 800) {
    return 3;
  }
  if (contentWidth > 460) {
    return 2;
  }
  return 1;
}

/**
 * Grid size for layout. An unknown width uses the SSR fallback so the first
 * render can emit article titles. A measured width keeps the 460px / 800px
 * breakpoints.
 */
export function getMagazineLayoutGridSize(
  measuredWidth: number | undefined
): number {
  if (measuredWidth === undefined) {
    return MAGAZINE_SSR_GRID_SIZE;
  }
  return getMagazineGridSize(measuredWidth);
}

/**
 * Articles passed to the layout engine. Later pages stay out of the SSR HTML
 * and the client's first render, then appear after mount.
 */
export function getMagazineArticlesForLayout<T>(
  articles: T[],
  includeLaterPages: boolean,
  pageSize = MAGAZINE_PAGE_SIZE
): T[] {
  if (includeLaterPages) {
    return articles;
  }
  return articles.slice(0, pageSize);
}
